import {
  APILoadingStatus,
  Map as GoogleMap,
  useApiLoadingStatus,
  useMap
} from '@vis.gl/react-google-maps'
import { useCallback, useEffect, useId, useState } from 'react'
import './LocationsMap.scss'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import LocationsMapMarkers from './LocationsMapMarkers'
import { ErrorBoundary } from 'react-error-boundary'
import { _x } from '@wordpress/i18n'
import { configActions } from '../store/configSlice'
import ERROR_MESSAGES from '../errors'
import { MapsApiProvider } from '../../../../assets/frontend/js/components/location-map/MapsApiProvider'

function GoogleMapInstance(props: {
  id: string
  onMapReady?: (map: google.maps.Map) => void
}) {
  const map = useMap(props.id)

  useEffect(() => {
    if (map) {
      props.onMapReady?.(map)
    }
  }, [map, props.onMapReady])

  return null
}

function Fallback() {
  const error = useAppSelector((state) => state.config.mapsError)

  return (
    <div className="location-finder__map-error">
      <h3>
        {_x('The map could not be loaded.', 'location finder error', 'icf')}
      </h3>
      <p className="fs-sm text-dark-4">
        {error ? error.message : ERROR_MESSAGES.GENERIC}
      </p>
    </div>
  )
}

export default function LocationsMap() {
  // const { refine } = useGeoSearch({})

  const id = `LOCATIONS_FINDER_${useId()}`
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null)
  const onMapReady = useCallback((m: google.maps.Map) => {
    setMapInstance(m)
  }, [])

  const userGeoLocation = useAppSelector((state) => state.config.geoLocation)
  const selectedLocation = useAppSelector((state) => state.filter.location)
  const mapsApiKey = useAppSelector((state) => state.config.mapsApiKey)
  const placeholderImageUrl = useAppSelector(
    (state) => state.config.placeholderImageUrl
  )
  const privacyPageUrl = useAppSelector((state) => state.config.privacyPageUrl)

  useEffect(() => {
    if (mapInstance && !!selectedLocation?.lat && !!selectedLocation?.lng) {
      mapInstance.setCenter({
        lat: selectedLocation.lat,
        lng: selectedLocation.lng
      })
      mapInstance.setZoom(12)
    }
  }, [selectedLocation, mapInstance])

  const status = useApiLoadingStatus()

  const error = useAppSelector((state) => state.config.mapsError)
  const [hasUserConsent, setHasUserConsent] = useState(
    window.cookiehub?.hasConsented('marketing') ?? false
  )

  const dispatch = useAppDispatch()

  const loadMap = () => {
    if (typeof window.cookiehub !== 'undefined') {
      window.cookiehub.allow('marketing')
      setHasUserConsent(window.cookiehub.hasConsented('marketing'))
      setTimeout(() => {
        window.cookiehub.closeDialog()
      }, 0)
    }
  }

  useEffect(() => {
    let error = null
    switch (status) {
      case APILoadingStatus.AUTH_FAILURE:
        error = new Error(ERROR_MESSAGES.AUTH_FAILURE)
        break
      case APILoadingStatus.FAILED:
        error = new Error()
        break
    }
    if (error) {
      dispatch(configActions.setMapsError(error))
    }
  }, [status, dispatch])

  return (
    <div className="location-finder__map">
      {!error ? (
        <ErrorBoundary FallbackComponent={Fallback}>
          {hasUserConsent ? (
            <MapsApiProvider mapsApiKey={mapsApiKey}>
              <GoogleMapInstance id={id} onMapReady={onMapReady} />
              <GoogleMap
                reuseMaps={true}
                defaultZoom={8}
                defaultCenter={userGeoLocation}
                id={id}
                mapId="db862b5789f82c78"
                mapTypeControl={false}
                fullscreenControl={false}
                streetViewControl={false}
                mapTypeId="roadmap"
              >
                <LocationsMapMarkers />
              </GoogleMap>
            </MapsApiProvider>
          ) : (
            <div className="position-relative" style={{ height: '100%' }}>
              <img
                src={placeholderImageUrl}
                alt="Map preview"
                style={{
                  height: '100%',
                  width: '100%',
                  filter: 'blur(4px) brightness(0.6)',
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
              <div
                className="position-absolute top-50 start-50 translate-middle text-center text-white"
                style={{ maxWidth: 400, width: '90%' }}
              >
                <p className="mb-3 fs-4">
                  {_x(
                    'By clicking, content from Google Maps loads. You allow cookies for marketing. Learn more in our',
                    'location finder placeholder',
                    'icf'
                  )}{' '}
                  <a href={privacyPageUrl} className="text-white">
                    {_x('Privacy Policy', 'location finder placeholder', 'icf')}
                  </a>
                  .
                </p>
                <button
                  type="button"
                  className="btn btn-black text-white"
                  onClick={loadMap}
                >
                  {_x('Show Map', 'location finder button', 'icf')}
                </button>
              </div>
            </div>
          )}
        </ErrorBoundary>
      ) : (
        <Fallback />
      )}
    </div>
  )
}
